/**
 * Wrapper around `@zoom/appssdk` that:
 *  - Calls `zoomSdk.config()` once with our declared capabilities.
 *  - Pulls meeting + user context after config succeeds.
 *  - Falls back gracefully when NOT running inside a Zoom client (the SDK
 *    rejects `config()` with a recognizable error). This lets the same
 *    bundle work as the Zoom side panel AND as a standalone webpage
 *    (the rescue path documented in DEMO_FAILURE_MODES.md style).
 */

import zoomSdk from '@zoom/appssdk'
import type { MeetingContext } from './types'

/**
 * Capabilities we declare. MUST match the Marketplace app's "Zoom App SDK"
 * capability checklist — declaring a capability here that's NOT enabled in
 * Marketplace causes config() to reject and Zoom shows "Something went wrong".
 *
 * Current set (7) maps to the Marketplace config:
 *   - getRunningContext         "Am I in a meeting / main client / webinar?"
 *   - getMeetingContext         meeting topic, host, start time
 *   - getMeetingParticipants    participant list (drives the chips below the title)
 *   - getUserContext            current user's display name
 *   - onParticipantChange       event listener for join/leave (live updates)
 *   - expandApp                 programmatically expand the side panel
 *   - openUrl                   open external URLs in user's default browser
 */
const CAPABILITIES = [
  'getRunningContext',
  'getMeetingContext',
  'getMeetingParticipants',
  'getUserContext',
  'onParticipantChange',
  'expandApp',
  'openUrl',
] as const

export interface ZoomEnvironment {
  /** True when the SDK successfully handshook with a real Zoom client. */
  inZoom: boolean
  /** "inMeeting" | "inMainClient" | "inWebinar" | ... */
  runningContext: string | null
  meetingContext: MeetingContext
  /** Display name of the user inside the Zoom client. */
  userDisplayName: string | null
}

let initialized: ZoomEnvironment | null = null
let initPromise: Promise<ZoomEnvironment> | null = null

export function getZoomEnvironment(): Promise<ZoomEnvironment> {
  if (initialized) return Promise.resolve(initialized)
  if (initPromise) return initPromise
  initPromise = init().then((env) => {
    initialized = env
    return env
  })
  return initPromise
}

async function init(): Promise<ZoomEnvironment> {
  // config() validates declared capabilities against the Marketplace app's
  // capability list. Mismatch → reject → Zoom shows "Something went wrong".
  // We don't pass `version`; the SDK negotiates with Zoom's server.
  try {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    await zoomSdk.config({ capabilities: [...CAPABILITIES] as any })
  } catch (err) {
    console.info('[zoom-sdk] config() failed — running standalone:', err)
    return standaloneEnv()
  }

  // Pull context in parallel. Each call individually safe-wrapped so a single
  // failure (e.g., a capability disabled in Marketplace mid-flight) doesn't
  // crash the whole init.
  const [running, meeting, user, participantsRes] = await Promise.all([
    safeCall(() => zoomSdk.getRunningContext()),
    safeCall(() => zoomSdk.getMeetingContext()),
    safeCall(() => zoomSdk.getUserContext()),
    safeCall(() => zoomSdk.getMeetingParticipants()),
  ])

  const participantNames =
    (participantsRes?.participants ?? [])
      .map((p) => p.screenName ?? '')
      .filter(Boolean) ||
    (user?.screenName ? [user.screenName] : [])

  return {
    inZoom: true,
    runningContext: running?.context ?? null,
    meetingContext: {
      title: meeting?.meetingTopic,
      participants: participantNames,
    },
    userDisplayName: user?.screenName ?? null,
  }
}

function standaloneEnv(): ZoomEnvironment {
  return {
    inZoom: false,
    runningContext: null,
    meetingContext: {},
    userDisplayName: null,
  }
}

async function safeCall<T>(fn: () => Promise<T>): Promise<T | null> {
  try {
    return await fn()
  } catch (err) {
    console.warn('[zoom-sdk] call failed:', err)
    return null
  }
}

/**
 * Subscribe to participant-change events. No-op when running standalone.
 * Returns an unsubscribe function.
 */
export function onParticipantChange(
  handler: (participants: string[]) => void,
): () => void {
  if (!initialized?.inZoom) return () => undefined

  const listener = () => {
    safeCall(() => zoomSdk.getMeetingParticipants()).then((res) => {
      const names = (res?.participants ?? [])
        .map((p) => p.screenName ?? '')
        .filter(Boolean)
      if (names.length) handler(names)
    })
  }
  zoomSdk.addEventListener('onParticipantChange', listener)
  return () => zoomSdk.removeEventListener('onParticipantChange', listener)
}

/** Best-effort expand of the side panel — only meaningful in-meeting. */
export async function expandSidePanel(): Promise<void> {
  if (!initialized?.inZoom) return
  await safeCall(() => zoomSdk.expandApp({ action: 'expand' }))
}
